import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Clientes } from 'app/interfaces/Cliente';
import { Fazendas } from 'app/interfaces/Fazenda';
import { Operacoes } from 'app/interfaces/Operacao';
import { ClienteService } from 'app/services/cliente/cliente.service';
import { CustomDateAdapter } from 'app/services/dataConfig/CustomDateAdapter';
import { DialogService } from 'app/services/dialog/dialog.service';
import { FazendaService } from 'app/services/fazenda/fazenda.service';
import { Observable, Subject, takeUntil, timer } from 'rxjs';
import { IconComponent } from "./icon/icon.component";
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

export const MY_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-filtro-mapa',
  standalone: true,
  providers: [[
    provideNativeDateAdapter(),
    { provide: DateAdapter, useClass: CustomDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' }
  ]],
  imports: [
    MatDatepickerModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    IconComponent,
    TranslocoModule
  ],
  templateUrl: './filtro-mapa.component.html',
  styleUrl: './filtro-mapa.component.css'
})
export class FiltroMapaComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly linguagensDatepicker: Record<string, string> = {
    en: 'en-US',
    'en-en': 'en-US',
    'en-us': 'en-US',
    pt: 'pt-BR',
    'pt-br': 'pt-BR',
    es: 'es-ES',
    'es-es': 'es-ES'
  };
  private readonly destroy$ = new Subject<void>();
  private readonly cacheFazendas = new Map<number, Fazendas>();
  public filtroAberto: boolean = false;
  public animacao: boolean = false;
  public dataMax!: Date;
  public clienteSelecionadoId: number = -1;
  public clientesDisponiveis!: Clientes;
  public fazendasDisponiveis!: Fazendas;
  public operacoesDisponiveis!: Operacoes;
  @Input() carregandoFiltros: boolean = false;
  @Output() filtrosAplicados: EventEmitter<any> = new EventEmitter<any>();

  public readonly formularioFiltros = new FormGroup({
    operacaoId: new FormControl<string | null>(null),
    fazendaId: new FormControl<string | null>(null),
    clienteId: new FormControl<string | null>(null),
    dataInicio: new FormControl<Date | null>(null),
    dataFim: new FormControl<Date | null>(null)
  });

  public constructor(
    private readonly clienteService: ClienteService,
    private readonly fazendaService: FazendaService,
    private readonly dialogService: DialogService,
    private readonly translocoService: TranslocoService,
    private adapter: DateAdapter<any>
  ) {
    this.mudarIdioma(this.obterIdiomaPreferido());
  }
  ngAfterViewInit(): void {

  }

  mudarIdioma(idioma: string): void {
    this.adapter.setLocale(this.obterLocaleDatepicker(idioma));
  }

  ngOnInit(): void {
    this.translocoService.langChanges$
      .pipe(takeUntil(this.destroy$))
      .subscribe(idioma => {
        this.mudarIdioma(idioma);
      });

    this.inicializarFiltros();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public alternarFiltro(): void {
    if (this.animacao) {
      this.filtroAberto = !this.filtroAberto;
      this.animacao = !this.animacao;
    } else {
      this.animacao = !this.animacao;
      timer(120).subscribe(() => {
        this.filtroAberto = !this.filtroAberto;
      });
    }
  }

  public atualizarDataMax(): void {
    const dataInicio: Date | null = this.formularioFiltros.get('dataInicio')?.value ?? null;
    if (!dataInicio) {
      this.dataMax = undefined!;
      return;
    }

    const novaDataMax: Date = new Date(dataInicio);
    novaDataMax.setMonth(novaDataMax.getMonth() + 2);
    this.dataMax = novaDataMax;
  }

  public aplicarFiltros(): void {
    const valores: any = this.formularioFiltros.value;
    const valoresConvertidos: any = Object.keys(valores).reduce((acc, key) => {
      if (key.endsWith('Id') && valores[key] !== 'null' && valores[key] !== null) {
        acc[key] = Number(valores[key]);
      } else if (key === 'dataInicio' || key === 'dataFim') {
        acc[key] = valores[key];
      }

      return acc;
    }, {} as any);

    localStorage.setItem('clienteId', valoresConvertidos.clienteId);

    if (valoresConvertidos.fazendaId) {
      localStorage.setItem('fazendaId', valoresConvertidos.fazendaId.toString());
    } else {
      localStorage.removeItem('fazendaId');
    }

    if (valoresConvertidos.operacaoId) {
      localStorage.setItem('operacaoId', valoresConvertidos.operacaoId.toString());
    } else {
      localStorage.removeItem('operacaoId');
    }

    const { dataInicio, dataFim } = this.formularioFiltros.value;
    if (dataInicio) localStorage.setItem('dataInicio', dataInicio.toISOString());
    if (dataFim) localStorage.setItem('dataFim', dataFim.toISOString());

    this.filtrosAplicados.emit(valoresConvertidos);
  }

  public onChangeCliente(event: Event): void {
    let clienteId: number = Number((event.target! as HTMLInputElement).value);
    this.clienteSelecionadoId = clienteId;

    this.fazendasDisponiveis = [];
    this.operacoesDisponiveis = [];
    this.formularioFiltros.patchValue({
      fazendaId: null,
      operacaoId: null,
    });

    localStorage.setItem('clienteId', clienteId.toString());
    this.buscarFazendas();
  }

  public onChangeFazenda(event: Event): void {
    const valor: string = (event.target! as HTMLInputElement).value;
    const fazendaId: number | undefined = (valor && valor !== 'null') ? Number(valor) : undefined;
    this.buscarOperacoes(fazendaId);
  }

  public buscarClientes(): void {
    this.clientesDisponiveis = JSON.parse(sessionStorage.getItem("clientes")!);
    let clienteIdLocalstorage: string | null = localStorage.getItem('clienteId');
    if (this.clientesDisponiveis) {
      this.clienteSelecionadoId = clienteIdLocalstorage ? Number.parseFloat(clienteIdLocalstorage) : this.clientesDisponiveis[0].id;
      this.formularioFiltros.get('clienteId')?.setValue(this.clienteSelecionadoId.toString());
      localStorage.setItem('clienteId', this.clienteSelecionadoId.toString());
      this.buscarFazendas(true);
    }
  }

  public buscarFazendas(restaurar: boolean = false): void {
    if (this.cacheFazendas.has(this.clienteSelecionadoId)) {
      const fazendas = this.cacheFazendas.get(this.clienteSelecionadoId)!;
      this.fazendasDisponiveis = fazendas;
      const fazendaId = restaurar ? this.restaurarFazendaId(fazendas) : undefined;
      this.buscarOperacoes(fazendaId, restaurar);
      return;
    }

    this.fazendaService.buscarNomesFazendasPorCliente(this.clienteSelecionadoId).subscribe({
      next: (response: any) => {
        const fazendas: Fazendas = Array.isArray(response) ? response : [];
        this.fazendasDisponiveis = fazendas;
        this.cacheFazendas.set(this.clienteSelecionadoId, fazendas);

        if (!fazendas || fazendas.length === 0) {
          this.dialogService.aviso(
            'Nenhuma fazenda encontrada',
            'O cliente selecionado não possui fazendas cadastradas. Cadastre uma fazenda para utilizar os filtros do mapa.'
          );
        }

        const fazendaId = restaurar ? this.restaurarFazendaId(fazendas) : undefined;
        this.buscarOperacoes(fazendaId, restaurar);
      },
      error: () => {
        this.fazendasDisponiveis = [];
        this.operacoesDisponiveis = [];
        this.dialogService.aviso(
          'Erro ao carregar fazendas',
          'Não foi possível carregar as fazendas do cliente selecionado. Tente novamente.'
        );
      }
    });
  }

  public buscarOperacoes(fazendaId?: number, restaurar: boolean = false): void {
    const operacoes$: Observable<Operacoes> = fazendaId
      ? this.fazendaService.buscarOperacoesPremocenter(fazendaId)
      : this.clienteService.buscarTodasOperacoes(this.clienteSelecionadoId);

    operacoes$.subscribe({
      next: (operacoes: Operacoes) => {
        this.operacoesDisponiveis = operacoes;

        if (!operacoes || operacoes.length === 0) {
          this.dialogService.aviso(
            'Nenhuma operação encontrada',
            fazendaId
              ? 'A fazenda selecionada não possui operações cadastradas.'
              : 'O cliente selecionado não possui operações cadastradas. Cadastre uma operação para utilizar os filtros do mapa.'
          );
        }

        if (restaurar) {
          this.restaurarOperacaoId(operacoes);
        }
      },
      error: () => {
        this.operacoesDisponiveis = [];
        this.dialogService.aviso(
          'Erro ao carregar operações',
          'Não foi possível carregar as operações. Tente novamente.'
        );
      }
    });
  }

  private inicializarFiltros(): void {
    this.buscarClientes();

    const hoje = new Date();
    const dataInicio = this.obterDataInicial();
    const dataFim = this.obterDataArmazenadaOuPadrao('dataFim', hoje);

    this.formularioFiltros.patchValue({
      dataInicio,
      dataFim
    });

    this.atualizarDataMax();
  }

  private obterLocaleDatepicker(idioma: string): string {
    const idiomaNormalizado = String(idioma || 'pt-BR').toLowerCase();
    return this.linguagensDatepicker[idiomaNormalizado] ?? idioma;
  }

  private obterIdiomaPreferido(): string {
    return localStorage.getItem('preferencia_linguagem')
      ?? localStorage.getItem('linguagemPreferida')
      ?? this.translocoService.getActiveLang()
      ?? 'pt-BR';
  }

  private obterDataInicial(): Date {
    const dataArmazenada = localStorage.getItem('dataInicio');
    if (dataArmazenada) {
      return new Date(dataArmazenada);
    }

    const dataPadrao = new Date();
    dataPadrao.setDate(dataPadrao.getDate() - 30);
    return dataPadrao;
  }

  private obterDataArmazenadaOuPadrao(chave: string, valorPadrao: Date): Date {
    const dataArmazenada = localStorage.getItem(chave);
    return dataArmazenada ? new Date(dataArmazenada) : valorPadrao;
  }

  private restaurarFazendaId(fazendas: Fazendas): number | undefined {
    const fazendaIdStorage = localStorage.getItem('fazendaId');
    if (!fazendaIdStorage) return undefined;

    const fazendaId = Number(fazendaIdStorage);
    const encontrada = fazendas.some(f => f.sr_id === fazendaId);
    if (encontrada) {
      this.formularioFiltros.patchValue({ fazendaId: fazendaIdStorage });
      return fazendaId;
    }
    return undefined;
  }

  private restaurarOperacaoId(operacoes: Operacoes): void {
    const operacaoIdStorage = localStorage.getItem('operacaoId');
    if (operacaoIdStorage) {
      const operacaoId = Number(operacaoIdStorage);
      const encontrada = operacoes.some(op => op.sr_id === operacaoId);
      if (encontrada) {
        this.formularioFiltros.patchValue({ operacaoId: operacaoIdStorage });
      }
    }

    this.aplicarFiltros();
  }
}
